/**
 * SigWeb bridge — the vendor half of the HYBRID signing model (CG-F, PROD-1064).
 *
 * Topaz signature pads (SigLite T-S460, SignatureGem, …) reach the browser
 * through **SigWeb**: a small local Windows service that ships a JavaScript
 * library (`SigWebTablet.js`) exposing global functions the page calls. The
 * pad talks to the service over USB; the service talks to the page over
 * `localhost`. This module is the thin, typed seam over those globals so the
 * rest of the app never touches `window` directly and stays unit-testable.
 *
 * IMPORTANT (on-device validation pending): the exact `SigWebTablet.js` surface
 * is version-sensitive — notably the `SetTabletState` arity and the async
 * `GetSigImageB64` callback shape. Everything here is written against Topaz's
 * documented API, but the physical pad lives on another machine, so the calls
 * marked `@onDevice` must be confirmed on hardware before this ships.
 *
 * Windows-only: SigWeb has no macOS/Linux build. On any host without it the
 * globals are absent → {@link getSigWeb} returns `null` → the Topaz provider
 * reports unavailable and the workflow falls back to the on-screen canvas.
 */

/**
 * The subset of the Topaz `SigWebTablet.js` global API we depend on. Kept
 * minimal on purpose — every added function is another on-device unknown.
 */
export interface SigWebApi {
  /** Pings the local SigWeb service. Present on modern builds; used as the
   *  primary liveness probe. Absent on very old builds → we fall back to a
   *  globals-present heuristic. */
  IsSigWebInstalled?: () => boolean;
  /** Blank the pad + internal buffer. */
  ClearTablet: () => void;
  /**
   * Start (`1`) / stop (`0`) capture. The 3-arg form binds a 2D canvas context
   * for live ink rendering and returns a timer handle; the stop call takes that
   * handle back. `@onDevice` — some builds use `SetTabletState(1, tmr)` with a
   * separately-created `Tablet`; confirm against the installed SigWebTablet.js.
   */
  SetTabletState: (
    state: number,
    ctxOrTimer?: CanvasRenderingContext2D | number,
    interval?: number,
  ) => number;
  /** Count of captured points — `0` ⇒ nothing signed yet. */
  NumberOfTabletPoints: () => number;
  /** Async (callback) fetch of the signature as a base64 PNG. `@onDevice`. */
  GetSigImageB64: (callback: (base64: string) => void) => void;
  /** Forensic `.SIG` biometric string (stroke/pressure data). */
  GetSigString?: () => string;
  /** Output image sizing / styling. */
  SetImageXSize: (px: number) => void;
  SetImageYSize: (px: number) => void;
  SetImagePenWidth?: (px: number) => void;
  SetJustifyMode?: (mode: number) => void;
  SetSigCompressionMode?: (mode: number) => void;
}

/** The window keys SigWebTablet.js defines, for the type-safe global read. */
type SigWebWindow = Window &
  Partial<Record<keyof SigWebApi, SigWebApi[keyof SigWebApi]>>;

/**
 * Resolve the SigWeb global API, or `null` when SigWebTablet.js hasn't loaded
 * (no service, non-Windows, or script not injected yet). Never throws.
 */
export function getSigWeb(win: Window = window): SigWebApi | null {
  const w = win as SigWebWindow;
  // The core capture functions must all be present; the optional ones
  // (`GetSigString`, sizing) are feature-detected at call sites.
  if (
    typeof w.SetTabletState !== 'function' ||
    typeof w.ClearTablet !== 'function' ||
    typeof w.NumberOfTabletPoints !== 'function' ||
    typeof w.GetSigImageB64 !== 'function'
  ) {
    return null;
  }
  return w as unknown as SigWebApi;
}

/**
 * Non-blocking presence check: are the SigWeb globals loaded? A pure `typeof`
 * read — it NEVER calls the local service, so a hung SigWeb service cannot
 * stall the caller. Prefer this on UI-render paths (e.g. probing on popup
 * open); the authoritative live-service check happens when capture actually
 * starts (`TopazSignatureProvider.start()` surfaces a connection error).
 */
export function isSigWebLoaded(win: Window = window): boolean {
  return getSigWeb(win) !== null;
}

/**
 * Is a Topaz pad usable right now? Prefers the service ping
 * (`IsSigWebInstalled`) when available; otherwise treats "globals present" as
 * usable. Always resolves — a throw here would be a broker "unavailable", but
 * we return a boolean so callers can log the distinction.
 */
export function isSigWebAvailable(win: Window = window): boolean {
  try {
    const api = getSigWeb(win);
    if (!api) return false;
    if (typeof api.IsSigWebInstalled === 'function') {
      return api.IsSigWebInstalled() === true;
    }
    // Old build with no ping: globals exist, assume usable.
    return true;
  } catch {
    return false;
  }
}

/**
 * Inject `SigWebTablet.js` from `scriptUrl` and resolve once the globals
 * appear. The file is Topaz-provided (freely redistributed for integration);
 * vendor it under `public/` or serve it from the SigWeb install. Resolves
 * `false` (never rejects) on load error or timeout so callers can fall back.
 *
 * No-ops (resolves `true`) if the globals are already present.
 */
export function loadSigWeb(
  scriptUrl: string,
  win: Window = window,
  timeoutMs = 4000,
): Promise<boolean> {
  if (getSigWeb(win)) return Promise.resolve(true);
  const doc = win.document;
  if (!doc) return Promise.resolve(false);

  return new Promise<boolean>((resolve) => {
    const existing = doc.querySelector<HTMLScriptElement>(
      `script[data-sigweb="1"]`,
    );
    const done = (ok: boolean) => resolve(ok && getSigWeb(win) !== null);

    if (existing) {
      // Another mount already injected it; poll briefly for the globals.
      const start = Date.now();
      const tick = () => {
        if (getSigWeb(win)) return resolve(true);
        if (Date.now() - start > timeoutMs) return resolve(false);
        win.setTimeout(tick, 100);
      };
      tick();
      return;
    }

    const script = doc.createElement('script');
    script.src = scriptUrl;
    script.async = true;
    script.dataset.sigweb = '1';
    const timer = win.setTimeout(() => done(false), timeoutMs);
    script.onload = () => {
      win.clearTimeout(timer);
      done(true);
    };
    script.onerror = () => {
      win.clearTimeout(timer);
      done(false);
    };
    doc.head.appendChild(script);
  });
}
